import api from "../utils/axiosInstance";
import * as actionTypes from "../constants/madeToMeasureConstants";
import {
  USER_FACING_FALLBACK,
  getApiErrorMessage,
} from "../utils/errorMessages";

const BASE_URL = "/made-to-measure";
const ADMIN_BASE_URL = "/admin/made-to-measure";

function safeText(value = "") {
  return String(value ?? "").trim();
}

function containsSensitiveCustomerCopy(value = "") {
  const text = safeText(value).toLowerCase();

  return (
    text.includes("secret") ||
    text.includes("token") ||
    text.includes("authorization") ||
    text.includes("cookie") ||
    text.includes("jwt") ||
    text.includes("mongo") ||
    text.includes("api key") ||
    text.includes("client_secret") ||
    text.includes("access_token") ||
    text.includes("refresh_token")
  );
}

function looksLikeRawProviderCode(value = "") {
  const text = safeText(value);

  return Boolean(text && /^[A-Z0-9_.:-]{8,}$/.test(text));
}

function customerSafeMessage(value = "", fallback = USER_FACING_FALLBACK) {
  const text = safeText(value);

  if (!text) return fallback;
  if (containsSensitiveCustomerCopy(text)) return fallback;
  if (looksLikeRawProviderCode(text)) return fallback;

  return text;
}

function normalizeApiError(error, fallback = USER_FACING_FALLBACK) {
  const data = error?.response?.data || {};
  const fallbackMessage = getApiErrorMessage(error, { fallback });
  const retryAfterSeconds = Number(
    data?.retryAfterSeconds ||
      error?.retryAfterSeconds ||
      error?.response?.headers?.["retry-after"] ||
      0,
  );

  return {
    message: customerSafeMessage(
      data?.customerMessage || data?.message || error?.normalizedMessage,
      fallbackMessage,
    ),
    code: safeText(data?.code || error?.errorCode || error?.code),
    status: Number(error?.response?.status || error?.statusCode || 0),
    requestId: safeText(
      data?.requestId ||
        error?.requestId ||
        error?.response?.headers?.["x-request-id"],
    ),
    retryAfterSeconds: Number.isFinite(retryAfterSeconds)
      ? Math.max(0, Math.ceil(retryAfterSeconds))
      : 0,
  };
}

function withDraftMutationStamp(payload = {}) {
  return {
    ...(payload && typeof payload === "object" ? payload : {}),
    __draftMutationStartedAt: new Date().toISOString(),
  };
}

export const getMadeToMeasureOptions = () => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_OPTIONS_REQUEST });

  try {
    const { data } = await api.get(`${BASE_URL}/options`);

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_OPTIONS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = normalizeApiError(
      error,
      "Could not load made to measure options.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_OPTIONS_FAIL,
      payload,
    });

    throw payload;
  }
};

export const getMadeToMeasureDraft = () => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_REQUEST });

  try {
    const { data } = await api.get(`${BASE_URL}/draft`);

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_DRAFT_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payload = normalizeApiError(
      error,
      "Could not load your made-to-measure draft. Please sign in and try again.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_DRAFT_FAIL,
      payload,
    });

    throw payload;
  }
};

export const updateMadeToMeasureDraftStep =
  (currentStep) => async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(`${BASE_URL}/draft/step`, {
        ...withDraftMutationStamp(),
        currentStep,
      });

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payload = normalizeApiError(
        error,
        "Could not save your made-to-measure step.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL,
        payload,
      });

      throw payload;
    }
  };

export const selectMadeToMeasureDraftProduct =
  (step, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${BASE_URL}/draft/selections/${encodeURIComponent(safeText(step))}`,
        withDraftMutationStamp(payload),
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not save this made-to-measure selection.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateMadeToMeasureDraftMeasurements =
  (payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${BASE_URL}/draft/measurements`,
        withDraftMutationStamp(payload),
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not save your measurements.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateMadeToMeasureDraftFulfillment =
  (payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${BASE_URL}/draft/fulfillment`,
        withDraftMutationStamp(payload),
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not save your delivery preference.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const updateMadeToMeasureDraftContact =
  (payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${BASE_URL}/draft/contact`,
        withDraftMutationStamp(payload),
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not save your contact details.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const submitMadeToMeasureDraft = () => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_REQUEST });

  try {
    const { data } = await api.post(
      `${BASE_URL}/draft/submit`,
      withDraftMutationStamp(),
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not submit your made-to-measure request.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const clearMadeToMeasureDraft =
  (draftId = "") =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_REQUEST });

    try {
      const suffix = safeText(draftId)
        ? `/${encodeURIComponent(safeText(draftId))}`
        : "";
      const { data } = await api.delete(`${BASE_URL}/draft${suffix}`);

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_SUCCESS,
        payload: data,
      });
      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_SUCCESS,
        payload: data,
      });
      dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_RESET });
      dispatch({ type: actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_RESET });
      dispatch({ type: actionTypes.MADE_TO_MEASURE_QUOTE_RESET });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not clear your made-to-measure draft.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const quoteMadeToMeasureConfiguration =
  (payload = {}, { signal } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_QUOTE_REQUEST });

    try {
      const { data } = await api.post(`${BASE_URL}/quote`, payload, {
        signal,
      });

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_QUOTE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      if (
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        signal?.aborted
      ) {
        return null;
      }

      const payloadError = normalizeApiError(
        error,
        "Could not load current pricing for your made-to-measure selections.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_QUOTE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const submitMadeToMeasureEnquiry = (payload = {}) => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_ENQUIRY_REQUEST });

  try {
    const { data } = await api.post(`${BASE_URL}/enquiries`, payload);

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_ENQUIRY_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not submit your made to measure enquiry.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_ENQUIRY_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const getMyMadeToMeasureRequests = () => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_MY_REQUESTS_REQUEST });

  try {
    const { data } = await api.get(`${BASE_URL}/my-requests`);

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_MY_REQUESTS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load your made to measure requests.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_MY_REQUESTS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const getMadeToMeasureRequest = (requestId) => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_REQUEST });

  try {
    const { data } = await api.get(
      `${BASE_URL}/requests/${encodeURIComponent(safeText(requestId))}`,
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load this made to measure request.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const createMadeToMeasureRequestPayment =
  (requestId, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_REQUEST });

    try {
      const { data } = await api.post(
        `${BASE_URL}/requests/${encodeURIComponent(safeText(requestId))}/payment`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not start payment for this request.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const createMadeToMeasureRequestSumUpCheckout =
  (requestId) => (dispatch) =>
    dispatch(
      createMadeToMeasureRequestPayment(requestId, {
        provider: "sumup",
      }),
    );

export const createMadeToMeasureRequestPayPalOrder =
  (requestId) => (dispatch) =>
    dispatch(
      createMadeToMeasureRequestPayment(requestId, {
        provider: "paypal",
      }),
    );

export const checkMadeToMeasureRequestPaymentStatus =
  (requestId, { provider = "" } = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_REQUEST });

    try {
      const query = safeText(provider)
        ? `?provider=${encodeURIComponent(safeText(provider))}`
        : "";
      const { data } = await api.get(
        `${BASE_URL}/requests/${encodeURIComponent(
          safeText(requestId),
        )}/payment-status${query}`,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not verify payment status for this request.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const captureMadeToMeasureRequestPayPalOrder =
  (requestId) => (dispatch) =>
    dispatch(
      checkMadeToMeasureRequestPaymentStatus(requestId, {
        provider: "paypal",
      }),
    );

export const getMadeToMeasureCustomerUpdate = (token) => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_REQUEST });

  try {
    const { data } = await api.get(
      `${BASE_URL}/enquiries/${encodeURIComponent(safeText(token))}`,
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load this made to measure update.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const respondToMadeToMeasureCustomerUpdateQuote =
  (token, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_REQUEST });

    try {
      const { data } = await api.post(
        `${BASE_URL}/enquiries/${encodeURIComponent(safeText(token))}/respond`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not save your quote response.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const acceptMadeToMeasureCustomerUpdateQuote =
  (token, payload = {}) =>
  async (dispatch) => {
    dispatch({
      type: actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_REQUEST,
    });

    try {
      const { data } = await api.post(
        `${BASE_URL}/enquiries/${encodeURIComponent(safeText(token))}/accept`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not accept this quote.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getAdminMadeToMeasureEnquiries = () => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_ADMIN_LIST_REQUEST });

  try {
    const { data } = await api.get(`${ADMIN_BASE_URL}/enquiries`);

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_ADMIN_LIST_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load made to measure enquiries.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_ADMIN_LIST_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const updateAdminMadeToMeasureEnquiry =
  (enquiryId, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_REQUEST });

    try {
      const { data } = await api.patch(
        `${ADMIN_BASE_URL}/enquiries/${encodeURIComponent(enquiryId)}`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not update this made to measure enquiry.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const sendMadeToMeasureQuote =
  (enquiryId, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_REQUEST });

    try {
      const { data } = await api.post(
        `${ADMIN_BASE_URL}/enquiries/${encodeURIComponent(enquiryId)}/send-quote`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not prepare the made to measure quote.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const getMadeToMeasureQuote = (token) => async (dispatch) => {
  dispatch({ type: actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_REQUEST });

  try {
    const { data } = await api.get(
      `${BASE_URL}/quotes/${encodeURIComponent(safeText(token))}`,
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_SUCCESS,
      payload: data,
    });

    return data;
  } catch (error) {
    const payloadError = normalizeApiError(
      error,
      "Could not load this made to measure quote.",
    );

    dispatch({
      type: actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_FAIL,
      payload: payloadError,
    });

    throw payloadError;
  }
};

export const acceptMadeToMeasureQuote =
  (token, payload = {}) =>
  async (dispatch) => {
    dispatch({ type: actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_REQUEST });

    try {
      const { data } = await api.post(
        `${BASE_URL}/quotes/${encodeURIComponent(safeText(token))}/accept`,
        payload,
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      const payloadError = normalizeApiError(
        error,
        "Could not accept this quote.",
      );

      dispatch({
        type: actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_FAIL,
        payload: payloadError,
      });

      throw payloadError;
    }
  };

export const resetMadeToMeasureOptions = () => ({
  type: actionTypes.MADE_TO_MEASURE_OPTIONS_RESET,
});

export const resetMadeToMeasureEnquiry = () => ({
  type: actionTypes.MADE_TO_MEASURE_ENQUIRY_RESET,
});

export const resetMadeToMeasureDraft = () => ({
  type: actionTypes.MADE_TO_MEASURE_DRAFT_RESET,
});

export const resetMadeToMeasureDraftUpdate = () => ({
  type: actionTypes.MADE_TO_MEASURE_DRAFT_UPDATE_RESET,
});

export const resetMadeToMeasureDraftSubmit = () => ({
  type: actionTypes.MADE_TO_MEASURE_DRAFT_SUBMIT_RESET,
});

export const resetMadeToMeasureDraftClear = () => ({
  type: actionTypes.MADE_TO_MEASURE_DRAFT_CLEAR_RESET,
});

export const resetMadeToMeasureQuote = () => ({
  type: actionTypes.MADE_TO_MEASURE_QUOTE_RESET,
});

export const resetMadeToMeasureMyRequests = () => ({
  type: actionTypes.MADE_TO_MEASURE_MY_REQUESTS_RESET,
});

export const resetMadeToMeasureRequestDetails = () => ({
  type: actionTypes.MADE_TO_MEASURE_REQUEST_DETAILS_RESET,
});

export const resetMadeToMeasureRequestPayment = () => ({
  type: actionTypes.MADE_TO_MEASURE_REQUEST_PAYMENT_RESET,
});

export const resetMadeToMeasurePaymentStatus = () => ({
  type: actionTypes.MADE_TO_MEASURE_PAYMENT_STATUS_RESET,
});

export const resetMadeToMeasureCustomerUpdate = () => ({
  type: actionTypes.MADE_TO_MEASURE_CUSTOMER_UPDATE_RESET,
});

export const resetMadeToMeasureAcceptCustomerUpdateQuote = () => ({
  type: actionTypes.MADE_TO_MEASURE_ACCEPT_CUSTOMER_UPDATE_RESET,
});

export const resetMadeToMeasureCustomerResponse = () => ({
  type: actionTypes.MADE_TO_MEASURE_CUSTOMER_RESPONSE_RESET,
});

export const resetMadeToMeasureAdminQuote = () => ({
  type: actionTypes.MADE_TO_MEASURE_ADMIN_QUOTE_RESET,
});

export const resetMadeToMeasureAdminUpdate = () => ({
  type: actionTypes.MADE_TO_MEASURE_ADMIN_UPDATE_RESET,
});

export const resetMadeToMeasurePublicQuote = () => ({
  type: actionTypes.MADE_TO_MEASURE_PUBLIC_QUOTE_RESET,
});

export const resetMadeToMeasureAcceptQuote = () => ({
  type: actionTypes.MADE_TO_MEASURE_ACCEPT_QUOTE_RESET,
});
